import When from "../../../components/When";
import {Col, Form, FormLabel, Row} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {PublishStatusType} from "../../../core/enums/enums";
import {useAcceptModalContext} from "../../../providers/AcceptModalProvider";
import {IDocument, IDocumentReadReceipt} from "../../../core/interfaces/document";
import {usePaginated} from "../../../hooks/usePaginated";
import {IPerson} from "../../../core/interfaces/common";
import {URL_COMPANY_USERS} from "../../../api/endpoints/endpoints";
import CustomMultiSelect from "../../../components/FormComponents/CustomMultiSelect";
import {useCreateReadReceipt, useGetReadReceipt, useMarkReadReceipt} from "../../../api/DocumentApi";
import {useGetUser} from "../../../hooks/useAuth";
import {DateToYYYYMMDD} from "../../../core/helpers/dates/datesHelper";
import {CustomDropdownItem} from "../../../components/CustomDropdown/CustomDropdownItem/CustomDropdownItem";
import {Icon} from "../../../assets/Icons/IconClassNames";
import {addToast} from "../../../store/slices/uiSlice";
import {toastInfo} from "../../../core/helpers/toastHelper";
import {useAppDispatch} from "../../../store/hooks";

const ReadReceiptModalBody = ({readReceipts}: { readReceipts: IDocumentReadReceipt[] }) => {
    return (<>
            <When condition={readReceipts.length > 0} otherwise={"Det finns inga läskvitton"}>
                {readReceipts.map(v => {
                    return <Row key={v.id}>
                        <Col>{v.user.name}</Col>
                        <Col>
                            <When condition={!!v.readAt} otherwise={"Ej läst"}>
                                {DateToYYYYMMDD(v.readAt)}
                            </When>
                        </Col>
                    </Row>
                })}
            </When>
        </>
    )
}

const CreateReadReceiptModalBody = ({currentReadReceipts, handleSelected}: {
    currentReadReceipts: IDocumentReadReceipt[],
    handleSelected: (user: { value: string, label: string }[]) => void
}) => {
    const user = useGetUser();
    const [sendToAll, setSendToAll] = useState(false);
    const [companyUsers, isFetching] = usePaginated<IPerson>(URL_COMPANY_USERS(user.companyId));

    const getFilteredMapped = () => {
        return companyUsers.data.filter(v => !currentReadReceipts.find(s => !s.readAt && s.user.id === v.id)).filter(v => v.id !== user.companyUserId).map(p => ({
            value: p.id,
            label: p.name
        })) ?? [];
    }
    const handleIt = (value: boolean) => {
        handleSelected(value ? getFilteredMapped() : [])
        setSendToAll(value)
    }
    const Multi = () => (<CustomMultiSelect
        isEditMode={true}
        disabled={sendToAll}
        name={"involvedUsers"}
        options={getFilteredMapped()}
        selected={[]}
        onChange={handleSelected}
    />)

    if (isFetching) return null

    return (
        <>
            <When condition={sendToAll} otherwise={<Multi/>}>
                <Multi/>
            </When>

            <Row className={"p-1"}>
                <Col sm={2}>
                    <Form.Switch disabled={getFilteredMapped().length == 0}
                                 onChange={({target}) => handleIt(target.checked)}/>
                </Col>
                <Col>
                    <FormLabel>Skicka till alla</FormLabel>
                </Col>
            </Row>
        </>
    )
}

export const CreateReadReceipt = (
    {
        currentReadReceipts,
        disabled,
        documentId,
        isEditMode,
    }: {
        currentReadReceipts: IDocumentReadReceipt[],
        disabled: boolean,
        documentId: string,
        isEditMode: boolean,
    }) => {
    const dispatch = useAppDispatch();
    const acceptModalContext = useAcceptModalContext();

    const user = useGetUser();
    const [, isFetching] = usePaginated<IPerson>(URL_COMPANY_USERS(user.companyId));

    const [chosenUsers, setChosenUsers] = useState<string[]>([]);

    const [request, isLoading] = useCreateReadReceipt(documentId);
    const handleSelected = (user: { value: string, label: string }[]) => {
        setChosenUsers(user.map(v => v.value));
    }

    useEffect(() => {
        acceptModalContext.disableAcceptButton(chosenUsers.length === 0);
        acceptModalContext.setAcceptFunction(() => request(chosenUsers));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chosenUsers]);

    const handleRequestReadReceipt = () => {
        if (isEditMode) {
            dispatch(addToast(toastInfo("Lämna redigeringsläge", "Vänligen lämna redigeringsläget.")));
            return;
        }
        acceptModalContext.open({
            header: "Begär läskvitto",
            declineButtonText: 'Avbryt',
            acceptButtonVariant: 'primary',
            acceptButtonText: "Spara",
            disableAcceptButton: true,
            body: <CreateReadReceiptModalBody currentReadReceipts={currentReadReceipts}
                                              handleSelected={handleSelected}/>
        })
    }

    return <CustomDropdownItem
        icon={Icon.FILE_LINES}
        text={"Begär läskvitto"}
        disabled={disabled || isFetching || isLoading}
        onClick={handleRequestReadReceipt}
    />
}

export const MarkReadReceipt = (
    {
        hide,
        document,
        disableQuery
    }: { hide: boolean, document: IDocument, disableQuery?: boolean }) => {
    const user = useGetUser();

    const [readReceipt, isFetchingReadReceipt] = useGetReadReceipt(document.id, !disableQuery);

    const [request, isFetching] = useMarkReadReceipt(document.id);
    return (
        <When
            condition={!hide && (document.publishStatus === PublishStatusType.APPROVED) && !!readReceipt.find(s => s.user.id === user.companyUserId && s.readAt == null)}>
            <CustomDropdownItem icon={Icon.EYE} text={"Markera som läst"}
                                disabled={isFetching || isFetchingReadReceipt}
                                onClick={() => request()}/>
        </When>
    )
}

export function ReadReceipt({document, isEditMode, disableQuery}: {
    document: IDocument,
    isEditMode: boolean,
    disableQuery?: boolean
}) {
    const [data, isFetching] = useGetReadReceipt(document.id, !disableQuery);
    const acceptModalContext = useAcceptModalContext();
    //only admins check later kan requesta och visa
    const commonDisabled = isFetching || (document.publishStatus !== PublishStatusType.APPROVED);
    const invalidStatusReadReceipts = [PublishStatusType.CREATED, PublishStatusType.AWAITING_APPROVAL];
    return (<>
            <CustomDropdownItem
                icon={Icon.FILE_LINES}
                text={"Läskvitton"}
                disabled={isFetching || invalidStatusReadReceipts.includes(document.publishStatus)}
                onClick={() => acceptModalContext.open({
                    header: "Läskvitton",
                    declineButtonText: 'Stäng',
                    size: "lg",
                    body: <ReadReceiptModalBody readReceipts={data}/>,
                    hideAcceptButton: true,
                })}
            />
            <CreateReadReceipt
                currentReadReceipts={data}
                documentId={document.id}
                disabled={document.archived || commonDisabled}
                isEditMode={isEditMode}
            />
        </>
    )
        ;
}